import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCcw, PackagePlus, Minus, Plus } from "lucide-react";
import { formatPrice } from "./SubscriptionTiersTab";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Assign a firm's subscription — TIER + ADD-ONS only.
 *
 * Deliberately trimmed: Status, Current period end, Trial ends, Billing
 * method / cycle / custom price / contact / notes all moved out of this
 * dialog. Subscription lifecycle is now driven by the firm's
 * subscription_start/end dates (set on Add/Edit ID Firm) and the paid
 * invoices under the per-firm Billing tab. Billing preferences live in
 * that same Billing tab.
 */
export default function AssignSubscriptionDialog({ workspace, onClose, onSaved }) {
  const [tiers, setTiers] = useState([]);
  const [addonsCatalog, setAddonsCatalog] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  // Keyed by addon_id → {enabled, quantity, unit_price_cents}
  const [firmAddons, setFirmAddons] = useState({});
  const [tierId, setTierId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, ac, s] = await Promise.all([
        api.get("/superadmin/subscription-tiers"),
        api.get("/superadmin/subscription-addons"),
        api.get(`/superadmin/workspaces`).then((r) => {
          const ws = (r.data.workspaces || []).find((w) => w.workspace_id === workspace.workspace_id);
          return { data: { subscription: ws?.subscription || null } };
        }),
      ]);
      setTiers((t.data.tiers || []).filter((x) => !x.archived));
      setAddonsCatalog((ac.data.addons || []).filter((x) => x.active !== false));
      const sub = s.data.subscription;
      setCurrent(sub);
      if (sub) {
        setTierId(sub.tier_id || "");
        const map = {};
        for (const a of sub.addons || []) {
          map[a.addon_id] = {
            enabled: !!a.enabled,
            quantity: a.quantity || 0,
            unit_price_cents: a.unit_price_cents ?? null,
          };
        }
        setFirmAddons(map);
      } else if (t.data.tiers?.length > 0) {
        setTierId(t.data.tiers[0].tier_id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [workspace]);

  useEffect(() => { load(); }, [load]);

  const selectedTier = tiers.find((t) => t.tier_id === tierId);

  const submit = async (e) => {
    e.preventDefault();
    if (!tierId) return toast.error("Pick a tier");
    setSaving(true);
    try {
      const addons = addonsCatalog
        .map((a) => {
          const st = firmAddons[a.addon_id];
          if (!st) return null;
          return {
            addon_id: a.addon_id,
            enabled: !!st.enabled,
            quantity: Math.max(0, st.quantity || 0),
            unit_price_cents: st.unit_price_cents == null ? null : Math.max(0, st.unit_price_cents),
          };
        })
        .filter(Boolean);
      const payload = { tier_id: tierId, addons };
      await api.post(`/superadmin/workspaces/${workspace.workspace_id}/subscription`, payload);
      toast.success("Subscription saved");
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reapplySnapshot = async () => {
    const ok = await confirm({
      title: "Refresh firm snapshot?",
      message: "Applies the CURRENT tier definition — any tier edits made since assignment will take effect for this firm.",
      confirmText: "Refresh",
    });
    if (!ok) return;
    try {
      await api.post(`/superadmin/workspaces/${workspace.workspace_id}/subscription/reapply-snapshot`);
      toast.success("Snapshot refreshed");
      load();
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent><Loader2 className="animate-spin mx-auto my-8 text-[#7A7571]" /></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="assign-subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage subscription — {workspace?.name}</DialogTitle>
          <DialogDescription>
            Pick a tier and set add-on quantities. Billing preferences and invoices are managed under the firm's <span className="font-semibold">Billing</span> menu.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 mt-2">
          {/* Tier selector */}
          <section className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Tier</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tiers.map((t) => (
                <label
                  key={t.tier_id}
                  className={`cursor-pointer p-3 rounded-2xl border transition-colors ${tierId === t.tier_id ? "border-[#E07A5F] bg-[#FDF3EE]" : "border-[#EBE5D9] bg-white"}`}
                  data-testid={`tier-radio-${t.slug}`}
                >
                  <input
                    type="radio" className="sr-only"
                    checked={tierId === t.tier_id}
                    onChange={() => setTierId(t.tier_id)}
                  />
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-semibold text-[#2C2A29]">{t.name}</div>
                    <div className="text-xs text-[#7A7571] whitespace-nowrap">{formatPrice(t.currency, t.price_monthly_cents)}</div>
                  </div>
                  {t.description && <div className="text-xs text-[#7A7571] mt-1">{t.description}</div>}
                </label>
              ))}
            </div>
          </section>

          {/* Add-ons — always-visible qty steppers */}
          {addonsCatalog.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <PackagePlus size={14} className="text-[#7A7571]" />
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Add-ons</div>
              </div>
              <p className="text-[11px] text-[#7A7571] -mt-1">Set a quantity above zero on any add-on to enable it for this firm.</p>
              <ul className="space-y-2">
                {addonsCatalog.map((a) => {
                  const st = firmAddons[a.addon_id] || { enabled: false, quantity: 0, unit_price_cents: null };
                  const qty = Math.max(0, Number(st.quantity) || 0);
                  const enabled = qty > 0;
                  const priceCents = st.unit_price_cents == null ? a.unit_price_cents : st.unit_price_cents;
                  const totalCents = enabled ? priceCents * qty : 0;
                  const bundleSize = Number(a.bundle_size || 1);
                  const isBundled = bundleSize > 1;
                  const bundleUnitWord = a.limit_key === "max_documents_per_project"
                    ? "document"
                    : a.limit_key === "max_archived_projects"
                    ? "project"
                    : "unit";
                  const perBundleLabel = isBundled ? `per ${bundleSize}-${bundleUnitWord} bundle` : "per unit";
                  const effectiveExtra = qty * bundleSize;
                  const setQty = (newQty) => setFirmAddons((s) => ({
                    ...s,
                    [a.addon_id]: {
                      enabled: newQty > 0,
                      quantity: newQty,
                      unit_price_cents: (s[a.addon_id] || {}).unit_price_cents ?? null,
                    },
                  }));
                  return (
                    <li
                      key={a.addon_id}
                      className={`rounded-2xl border p-3 transition-colors ${enabled ? "border-[#E07A5F] bg-[#FDF3EE]" : "border-[#EBE5D9] bg-white"}`}
                      data-testid={`firm-addon-${a.slug}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-[#2C2A29]">{a.name}</div>
                            {isBundled && (
                              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">
                                {bundleSize}-pack
                              </span>
                            )}
                          </div>
                          {a.description && <div className="text-xs text-[#7A7571] mt-0.5">{a.description}</div>}
                          <div className="text-[11px] text-[#7A7571] mt-1">
                            Reference: <span className="font-mono">{a.currency} {(a.unit_price_cents / 100).toFixed(2)}</span> {perBundleLabel}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">{isBundled ? "Bundles" : "Qty"}</div>
                          <div className="inline-flex items-stretch rounded-full border border-[#EBE5D9] bg-white overflow-hidden">
                            <button type="button" onClick={() => setQty(Math.max(0, qty - 1))} disabled={qty <= 0}
                              data-testid={`firm-addon-qty-dec-${a.slug}`}
                              className="px-2.5 py-1.5 text-[#4B4642] hover:bg-[#F7F4EC] disabled:opacity-40"><Minus size={14} /></button>
                            <input type="number" min={0} value={qty}
                              onChange={(e) => setQty(Math.max(0, Number(e.target.value) || 0))}
                              data-testid={`firm-addon-qty-${a.slug}`}
                              className="w-14 text-center font-semibold text-[#2C2A29] bg-transparent border-x border-[#EBE5D9] focus:outline-none focus:bg-[#F7F4EC]" />
                            <button type="button" onClick={() => setQty(qty + 1)}
                              data-testid={`firm-addon-qty-inc-${a.slug}`}
                              className="px-2.5 py-1.5 text-[#4B4642] hover:bg-[#F7F4EC]"><Plus size={14} /></button>
                          </div>
                          {isBundled && qty > 0 && (
                            <div className="text-[11px] text-[#7A7571]">= {effectiveExtra.toLocaleString()} extra {bundleUnitWord}s</div>
                          )}
                        </div>
                      </div>
                      {enabled && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3 border-t border-[#F6D2C2]">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">
                              Unit price ({a.currency}) {isBundled && <span className="normal-case font-normal">per bundle</span>}
                            </div>
                            <div className="rounded-2xl border border-[#EBE5D9] bg-white flex items-stretch">
                              <span className="px-3 flex items-center text-[#7A7571] text-sm border-r border-[#EBE5D9] font-mono">{a.currency}</span>
                              <input type="number" min={0} step="0.01" value={(priceCents / 100).toFixed(2)}
                                onChange={(e) => setFirmAddons((s) => ({
                                  ...s,
                                  [a.addon_id]: {
                                    ...s[a.addon_id],
                                    unit_price_cents: e.target.value === "" ? null : Math.max(0, Math.round(Number(e.target.value) * 100)),
                                  },
                                }))}
                                data-testid={`firm-addon-price-${a.slug}`}
                                className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-[#2C2A29]" />
                            </div>
                          </div>
                          <div className="text-right flex sm:items-end sm:justify-end">
                            <div>
                              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Monthly total</div>
                              <div className="font-display text-xl font-bold text-[#E07A5F]" data-testid={`firm-addon-total-${a.slug}`}>
                                {a.currency} {(totalCents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[#EBE5D9]">
            {current && (
              <button type="button" onClick={reapplySnapshot}
                data-testid="reapply-snapshot-btn"
                className="text-xs inline-flex items-center gap-1.5 text-[#7A7571] hover:text-[#2C2A29]">
                <RefreshCcw size={12} /> Re-apply current tier definition
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button type="button" onClick={onClose} className="btn-ghost" data-testid="assign-sub-cancel">Cancel</button>
              <button type="submit" disabled={saving} data-testid="assign-sub-save"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin" />} Save subscription
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
